import React, {useEffect} from 'react';
import {BrowserRouter, HashRouter} from "react-router-dom";
import {RecoilRoot} from "recoil";
import {ApplicationDataProvider} from "./providers/access/ApplicationDataProvider";
import {AppContextProvider} from "./providers/access/AppContextProvider";
import {GraphQLProvider} from "./providers/GraphQLProvider";
import {LogoutProvider} from "./providers/LogoutProvider";
import {LocalizationProvider} from "./providers/localization/LocalizationProvider";
import {PageRouter} from "./pages/PageRouter";
import {LayoutProvider} from "./providers/layout/LayoutProvider";
import {DialogProvider} from "./Dialog";
import {AccessForm} from "./pages/public/AccessForm";
import {ReturnsPagePath} from "./pages/restricted/returns/ReturnsPage";
import {AnalyticsProvider} from "./analytics/AnalyticsProvider";
import {Box} from "@mui/material";

declare global {
   interface Window {
      /** Use to lock app usage to a specific brand (consumers from all shops below can login) */
      brandId: string;
      /**
       * We need this on the frontpage, after the frontpage - the customers shop will take effect if brandId is set
       * otherwise this is required
       */
      shopId: string;

      /**
       * Multi login means that ANY create2STAY store's consumer can login here
       */
      multiLogin?: boolean

      /**
       * True, then the tradein app will load a full sized div with content to keep the browser from locking up
       */
      iosScrollFix?: boolean

      baseUrl?: string;
      disableLogin?: boolean;
      loginMode?: boolean;

      hashRouter?: boolean

      /**
       * Optionally an access token to load into our app
       */
      accessToken?: string

      /**
       * Optionally let the tradein app scroll to the identified top position
       */
      appDetectScrollTop?: boolean
      /**
       * Optionally, if set this will scroll the page so
       */
      appScrollIntoView?: boolean
      /**
       * Optionally, top offset
       */
      appScrollOffsetY?: number

      /**
       * Optionally a mode switch that can change the app per customer
       */
      mode?: string

      /**
       * Headless view - no frontpage is shown
       */
      headless?: boolean

      /**
       * Shopify support
       */
      Shopify?: {
         shop?: string
         locale?: string
         currency?: {
            active?: string
         }
         country?: string
      }

      tradeInSession?: {
         language?: string
         currency?: string
         countryCode?: string
      }

      useExternalCustomerData?: boolean
   }
}

const consumerAuth = /(.*?)[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(.*)/

const baseAppsRetrade = "apps/retrade";

function App() {
   useEffect(() => {
      if (document) {
         const body = document.body

         // Remove the sleeknote-anchor element if it exists
         const removeSleeknote = () => {
            const sleeknote = document.querySelector('.sleeknote-anchor');
            if (sleeknote) {
               sleeknote.remove();
            }
         };
         // Initial check in case the element is already present
         removeSleeknote();

         const observer = new MutationObserver(() => {
            removeSleeknote();
            body.style.touchAction = body.style.overflow === 'hidden' ? 'none' : ''
         })

         observer.observe(body, {
            attributes: true,
            attributeFilter: ['style'],
            childList: true, // Monitor for added/removed nodes
            subtree: true    // Monitor entire subtree
         })

         return () => observer && observer.disconnect();
      }
   }, [])

   if (!window.brandId && !window.shopId) {
      return <div>App is misconfigured, missing shopId</div>;
   }

   let basename = window.location.pathname;

   const matchResult = basename.match(consumerAuth);
   if (matchResult && matchResult.length > 1) {
      if (matchResult[1].startsWith("/")) {
         basename = matchResult[1];
      } else {
         basename = "/" + matchResult[1]
      }
   } else if (basename.includes(baseAppsRetrade)) {
      basename = basename.substring(0, basename.indexOf(baseAppsRetrade) + baseAppsRetrade.length)
   } else if (!window.hashRouter || window.shopId === '64219aae3d85b1c429993cef') {
      const appMainPath = ReturnsPagePath();
      if (basename.includes(appMainPath)) {
         basename = basename.substring(0, basename.indexOf(appMainPath))
      }
   }

   const Router = ({children}: { children: React.ReactNode | React.ReactNode[]; }) => {
      if (window.hashRouter && window.shopId !== '64219aae3d85b1c429993cef') {
         return <HashRouter>{children}</HashRouter>
      } else {
         return <BrowserRouter basename={basename}>{children}</BrowserRouter>
      }
   }

   const IosFixer = ({children}: { children: React.ReactNode | React.ReactNode[]; }) => {
      if (window.iosScrollFix) {
         return <Box sx={{
            height: "100vh",
            overflowY: "auto",
            "-webkit-overflow-scrolling": "touch",
            width: '100%'
         }}>
            {children}
            <Box sx={{
               height: '100vh'
            }}>&nbsp;</Box>
         </Box>
      } else {
         return <>{children}</>
      }
   }

   return (
      <IosFixer>
         <RecoilRoot>
            <Router>
               <AnalyticsProvider>
                  <AppContextProvider>
                     <LogoutProvider>
                        <GraphQLProvider>
                           <ApplicationDataProvider>
                              <LocalizationProvider>
                                 <LayoutProvider>
                                    <DialogProvider>
                                       {window.loginMode &&
                                          <div style={{display: 'flex', flexDirection: 'column', width: '100%'}}>
                                             <AccessForm/>
                                          </div>}
                                       {!window.loginMode && <PageRouter/>}
                                    </DialogProvider>
                                 </LayoutProvider>
                              </LocalizationProvider>
                           </ApplicationDataProvider>
                        </GraphQLProvider>
                     </LogoutProvider>
                  </AppContextProvider>
               </AnalyticsProvider>
            </Router>
         </RecoilRoot>
      </IosFixer>
   );
}

export default App;
